import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, Target, Heart, Award, Globe, BookOpen, Vote, BarChart3, Newspaper, Gamepad2, MapPin, History, ChevronRight, ExternalLink } from "lucide-react";

const StatCard = ({ icon: Icon, value, label, color }) => (
  <motion.div
    whileHover={{ y: -5 }}
    className="bg-white/5 border border-white/10 rounded-xl p-6 text-center"
  >
    <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center`} style={{ backgroundColor: `${color}20` }}>
      <Icon style={{ color }} size={24} />
    </div>
    <p className="font-bebas text-3xl text-white">{value}</p>
    <p className="text-gray-400 text-sm font-inter">{label}</p>
  </motion.div>
);

const FeatureCard = ({ icon: Icon, title, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ scale: 1.02 }}
    className="bg-white/5 border border-white/10 rounded-xl p-6"
  >
    <div className="w-12 h-12 bg-marigold/20 rounded-xl flex items-center justify-center mb-4">
      <Icon className="text-marigold" size={24} />
    </div>
    <h3 className="font-bebas text-xl text-white tracking-wider mb-2">{title}</h3>
    <p className="text-gray-400 text-sm font-inter leading-relaxed">{description}</p>
  </motion.div>
);

const TeamMember = ({ name, role, description }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-xl p-6 text-center"
  >
    <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-gorkhali-red to-peace-blue rounded-full flex items-center justify-center">
      <span className="font-bebas text-3xl text-white">{name.charAt(0)}</span>
    </div>
    <h3 className="font-bebas text-xl text-white tracking-wider">{name}</h3>
    <p className="text-marigold text-sm font-inter mb-2">{role}</p>
    <p className="text-gray-400 text-xs font-inter">{description}</p>
  </motion.div>
);

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-night-count">
      {/* Hero Section */}
      <div className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-gorkhali-red/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-peace-blue/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.div
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <Users className="mx-auto mb-6 text-marigold" size={64} />
            </motion.div>
            <h1 className="font-bebas text-6xl sm:text-7xl text-white tracking-wider mb-4" style={{ textShadow: "0 0 60px rgba(247,127,0,0.5)" }}>
              ABOUT NEPALI BALLOT
            </h1>
            <p className="font-playfair text-xl text-gray-300 italic max-w-2xl mx-auto">
              "Empowering citizens to understand and engage with Nepal's democratic process through education and simulation"
            </p>
          </motion.div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-6xl mx-auto px-4 -mt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={MapPin} value="165" label="Constituencies" color="#D90429" />
          <StatCard icon={Users} value="658+" label="Candidates" color="#F77F00" />
          <StatCard icon={Globe} value="7" label="Provinces" color="#2563EB" />
          <StatCard icon={History} value="70+" label="Years of Data" color="#059669" />
        </div>
      </div>

      {/* Mission Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-8"
        >
          <div className="bg-gradient-to-br from-gorkhali-red/20 to-gorkhali-red/5 border border-gorkhali-red/30 rounded-2xl p-8">
            <Target className="text-gorkhali-red mb-4" size={40} />
            <h2 className="font-bebas text-3xl text-white tracking-wider mb-4">OUR MISSION</h2>
            <p className="text-gray-300 font-inter leading-relaxed">
              To create an accessible, engaging platform that educates Nepali citizens and the global community about Nepal's democratic process. We believe that an informed electorate is the foundation of a strong democracy.
            </p>
          </div>
          <div className="bg-gradient-to-br from-peace-blue/20 to-peace-blue/5 border border-peace-blue/30 rounded-2xl p-8">
            <Heart className="text-peace-blue mb-4" size={40} />
            <h2 className="font-bebas text-3xl text-white tracking-wider mb-4">OUR VALUES</h2>
            <ul className="text-gray-300 font-inter space-y-2">
              <li className="flex items-center gap-2"><span className="text-marigold">•</span> Transparency in information</li>
              <li className="flex items-center gap-2"><span className="text-marigold">•</span> Non-partisan presentation</li>
              <li className="flex items-center gap-2"><span className="text-marigold">•</span> Accuracy and fact-checking</li>
              <li className="flex items-center gap-2"><span className="text-marigold">•</span> Accessibility for all users</li>
              <li className="flex items-center gap-2"><span className="text-marigold">•</span> Respect for democratic values</li>
            </ul>
          </div>
        </motion.div>
      </div>

      {/* What We Offer */}
      <div className="bg-black/30 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-bebas text-4xl text-white tracking-wider mb-2">WHAT WE OFFER</h2>
            <p className="text-gray-400 font-inter">Comprehensive tools to understand Nepal's electoral landscape</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard 
              icon={Vote}
              title="ELECTION SIMULATION"
              description="Participate in a realistic election simulation. Cast your vote for Prime Minister and see how others are voting in real-time."
            />
            <FeatureCard 
              icon={Users}
              title="CANDIDATE DATABASE"
              description="Access comprehensive profiles of 658+ candidates across all 165 constituencies, including party affiliations and backgrounds."
            />
            <FeatureCard 
              icon={MapPin}
              title="CONSTITUENCY EXPLORER"
              description="Explore all 165 federal constituencies across 77 districts and 7 provinces with detailed information and candidate lists."
            />
            <FeatureCard 
              icon={BarChart3}
              title="LIVE RESULTS"
              description="Track simulation results in real-time with dynamic charts, statistics, and analysis of voting patterns."
            />
            <FeatureCard 
              icon={History}
              title="ELECTION HISTORY"
              description="Learn about Nepal's democratic journey from 2008 to present, including past election results and political milestones."
            />
            <FeatureCard 
              icon={Newspaper}
              title="NEWS AGGREGATION"
              description="Stay informed with curated news from trusted sources including BBC Nepali, Kathmandu Post, and international outlets."
            />
          </div>
        </div>
      </div>

      {/* Disclaimer Box */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-marigold/10 border-2 border-marigold/40 rounded-2xl p-8 text-center"
        >
          <Award className="mx-auto mb-4 text-marigold" size={48} />
          <h2 className="font-bebas text-3xl text-white tracking-wider mb-4">IMPORTANT DISCLAIMER</h2>
          <p className="text-gray-300 font-inter max-w-2xl mx-auto leading-relaxed">
            Nepali Ballot is an <strong className="text-white">independent, non-governmental platform</strong> created for educational and entertainment purposes. We are not affiliated with the Election Commission of Nepal or any political party. Votes cast on this platform are simulations and have no legal effect on actual elections.
          </p>
          <Link 
            to="/disclaimer" 
            className="inline-flex items-center gap-2 mt-4 text-marigold hover:underline font-inter"
          >
            Read Full Disclaimer <ChevronRight size={16} />
          </Link>
        </motion.div>
      </div>

      {/* Data Sources */}
      <div className="bg-white/5 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <BookOpen className="mx-auto mb-4 text-peace-blue" size={40} />
            <h2 className="font-bebas text-3xl text-white tracking-wider mb-2">OUR DATA SOURCES</h2>
            <p className="text-gray-400 font-inter">We aggregate information from trusted, verified sources</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              { name: "Election Commission of Nepal", url: "https://election.gov.np", desc: "Official election data" },
              { name: "Kathmandu Post", url: "https://kathmandupost.com", desc: "News and candidate profiles" },
              { name: "BBC Nepali", url: "https://bbc.com/nepali", desc: "News coverage" },
              { name: "OpenNepal", url: "https://opennepal.net", desc: "Open data resources" },
            ].map((source, i) => (
              <motion.a
                key={i}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg p-4 hover:border-marigold/50 transition-all"
              >
                <div>
                  <p className="text-white font-bebas tracking-wider">{source.name}</p>
                  <p className="text-gray-500 text-xs font-inter">{source.desc}</p>
                </div>
                <ExternalLink className="text-gray-400" size={18} />
              </motion.a>
            ))}
          </div>

          <div className="text-center mt-6">
            <Link 
              to="/sources" 
              className="inline-flex items-center gap-2 text-marigold hover:underline font-inter"
            >
              View All Sources <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-bebas text-4xl text-white tracking-wider mb-4">GET IN TOUCH</h2>
          <p className="text-gray-400 font-inter mb-6">
            Have questions, feedback, or suggestions? We'd love to hear from you!
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-marigold hover:bg-marigold/80 text-peace-blue px-8 py-4 rounded-full font-bebas text-lg tracking-wider transition-all"
          >
            CONTACT US <ChevronRight size={20} />
          </Link>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <Link to="/privacy" className="text-gray-400 hover:text-marigold text-sm font-inter">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-marigold text-sm font-inter">Terms of Service</Link>
            <Link to="/disclaimer" className="text-gray-400 hover:text-marigold text-sm font-inter">Disclaimer</Link>
            <Link to="/sources" className="text-gray-400 hover:text-marigold text-sm font-inter">Sources</Link>
            <Link to="/contact" className="text-gray-400 hover:text-marigold text-sm font-inter">Contact</Link>
          </div>
          <p className="text-center text-gray-500 text-sm">© 2026 Nepali Ballot • Made with ❤️ for Nepal's Democracy</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
